// src/modules/assetManagement/library/AssetDetail.tsx
// Open an Asset: plays video/audio or opens the image large, and shows every Event and Product it is linked to.
import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { CalendarDays, ExternalLink, FileText, FolderCog, Link2, Maximize2, Package, Snowflake, X } from "lucide-react";
import { assetApi, RESTORE_ESTIMATE } from "../assetApi";
import Breadcrumbs from "../components/Breadcrumbs";
import { LinkModal } from "../components/AssetModals";
import { Button, EmptyState, StatusBadge, StorageBadge, useToast } from "../components/ui";
import type { Asset, LibraryProduct } from "../types";
import { useAssetQuery } from "../useAssetQuery";
import ManageAssetPanel from "../management/ManageAssetPanel";
import { ENTITY, extensionOf, formatBytes, formatDate, formatDuration, ROUTES, STATUS_META, trailFrom } from "../utils";

type DetailContext = "library" | "management";

/** The product record Product details expects in router state */
const toProductDetailsState = (p: LibraryProduct) => ({
  id: p.id,
  name: p.title,
  title: p.title,
  description: p.description ?? "",
  launchDate: p.launchDate ?? "",
  endDate: p.endDate ?? "",
  notes: p.notes ?? "",
  status: p.status,
  tags: p.tags,
  image: "",
});

/** Breadcrumb that leads into Asset Management, used when jumping there from the Library */
const MGMT_TRAIL = [{ label: "Asset Management", to: ROUTES.management }];

const Lightbox: React.FC<{ src: string; alt: string; onClose: () => void }> = ({ src, alt, onClose }) => {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);
  return (
    <div className="fixed inset-0 z-[120] bg-black/90 flex items-center justify-center p-6" onClick={onClose}>
      <button className="absolute top-4 right-4 text-white/80 hover:text-white p-2" aria-label="Close" onClick={onClose}>
        <X size={24} />
      </button>
      <img src={src} alt={alt} className="max-w-full max-h-full object-contain" onClick={(e) => e.stopPropagation()} />
    </div>
  );
};

const PreviewMessage: React.FC<{ icon: React.ReactNode; title: string; hint: string; action?: React.ReactNode }> = ({ icon, title, hint, action }) => (
  <div className="h-full min-h-[360px] flex flex-col items-center justify-center text-center p-8 bg-slate-50">
    <div className="w-14 h-14 rounded-full bg-white border border-slate-200 text-slate-400 flex items-center justify-center mb-3">{icon}</div>
    <p className="font-medium text-slate-800">{title}</p>
    <p className="text-sm text-slate-500 mt-1 max-w-sm">{hint}</p>
    {action && <div className="mt-4">{action}</div>}
  </div>
);

const AssetPlayer: React.FC<{ asset: Asset; inManagement: boolean }> = ({ asset, inManagement }) => {
  const [zoom, setZoom] = useState(false);
  const [failed, setFailed] = useState(false);
  const manageLink = inManagement ? undefined : (
    <Link to={ROUTES.managedAsset(asset.id)} state={{ trail: MGMT_TRAIL }}>
      <Button size="sm" icon={<FolderCog size={14} />}>Open in Asset Management</Button>
    </Link>
  );

  if (asset.storage !== "standard") {
    return (
      <PreviewMessage
        icon={<Snowflake size={24} />}
        title={asset.storage === "restoring" ? "Restoring from the archive" : "This asset is archived"}
        hint={
          asset.storage === "restoring"
            ? `It will be viewable again once the restore finishes (typically ${RESTORE_ESTIMATE}).`
            : "Archived files are kept in cold storage and can't be played. Restore it from Asset Management."
        }
        action={asset.storage === "archived" ? manageLink : undefined}
      />
    );
  }
  if (asset.status !== "completed") {
    const failedState = asset.status === "failed" || asset.status === "rejected";
    return (
      <PreviewMessage
        icon={<FileText size={24} />}
        title={failedState ? `${STATUS_META[asset.status].label}: no viewable copy` : "Still processing"}
        hint={asset.errorMessage ?? "A preview appears here once processing finishes."}
        action={failedState ? manageLink : undefined}
      />
    );
  }
  if (!asset.previewUrl || failed) {
    return (
      <PreviewMessage
        icon={<FileText size={24} />}
        title={`${extensionOf(asset.filename).toUpperCase()} file`}
        hint={failed ? "The preview couldn't be loaded. Try again later or get support from Asset Management." : "This file type has no in-browser preview."}
      />
    );
  }

  if (asset.kind === "video") {
    return <video key={asset.previewUrl} src={asset.previewUrl} controls className="w-full max-h-[70vh] bg-black" onError={() => setFailed(true)} />;
  }
  if (asset.kind === "audio") {
    return (
      <div className="min-h-[300px] flex flex-col items-center justify-center gap-6 p-8 bg-pink-50/50">
        <div className="w-24 h-24 rounded-2xl bg-pink-100 text-pink-500 flex items-center justify-center text-3xl font-bold">♪</div>
        <audio src={asset.previewUrl} controls className="w-full max-w-md" onError={() => setFailed(true)} />
      </div>
    );
  }
  if (asset.kind === "image") {
    return (
      <>
        <button type="button" onClick={() => setZoom(true)} className="relative block w-full bg-slate-900 group" aria-label="Open image large">
          <img src={asset.previewUrl} alt={asset.filename} className="w-full max-h-[70vh] object-contain" onError={() => setFailed(true)} />
          <span className="absolute top-3 right-3 inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-black/60 text-white text-xs opacity-0 group-hover:opacity-100 transition">
            <Maximize2 size={13} /> View large
          </span>
        </button>
        {zoom && <Lightbox src={asset.previewUrl} alt={asset.filename} onClose={() => setZoom(false)} />}
      </>
    );
  }
  return (
    <PreviewMessage
      icon={<FileText size={24} />}
      title={`${extensionOf(asset.filename).toUpperCase()} document`}
      hint="Open the file in a new tab to read it."
      action={
        <a href={asset.previewUrl} target="_blank" rel="noreferrer">
          <Button size="sm" icon={<ExternalLink size={14} />}>Open file</Button>
        </a>
      }
    />
  );
};

const DetailRow: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div className="flex justify-between gap-4 py-2 text-sm">
    <dt className="text-slate-500">{label}</dt>
    <dd className="text-slate-800 text-right min-w-0 break-words">{children}</dd>
  </div>
);

const AssetDetail: React.FC<{ context?: DetailContext }> = ({ context = "library" }) => {
  const { assetId = "" } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const inManagement = context === "management";
  const fallback = inManagement
    ? { label: "Asset Management", to: ROUTES.management }
    : { label: "Assets", to: ROUTES.libraryAssets };
  const { data: asset, initialLoading, error: loadError } = useAssetQuery(() => assetApi.getAsset(assetId), [assetId]);
  const { data: events = [] } = useAssetQuery(() => assetApi.listEvents(), []);
  const { data: products = [] } = useAssetQuery(() => assetApi.listProducts(), []);
  const [linkOpen, setLinkOpen] = useState(false);
  const { toastNode, showToast } = useToast();

  // After a delete, return to the list the user came from
  const goBackToList = () => {
    const trail = trailFrom(location.state);
    const target = trail.length ? trail[trail.length - 1] : fallback;
    navigate(target.to, { replace: true, state: { trail: trail.slice(0, -1) } });
  };

  if (initialLoading) return <div className="h-96 rounded-2xl bg-slate-200/60 animate-pulse" />;
  if (!asset) {
    return (
      <>
        <Breadcrumbs current="Not found" fallback={fallback} />
        <EmptyState
          icon={<FileText size={22} />}
          title={loadError ? "Couldn't load this asset" : "This asset no longer exists"}
          hint={loadError ?? "It may have been deleted in Asset Management."}
        />
      </>
    );
  }

  const linkedEvents = events.filter((e) => asset.eventIds.includes(e.id));
  const linkedProducts = products.filter((p) => asset.productIds.includes(p.id));
  const dims = asset.width && asset.height ? `${asset.width} × ${asset.height}` : null;

  return (
    <div>
      {toastNode}
      <Breadcrumbs current={asset.filename} fallback={fallback} />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-4 min-w-0">
          <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-sm">
            <AssetPlayer asset={asset} inManagement={inManagement} />
          </div>
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="min-w-0">
              <h2 className="text-xl font-semibold text-slate-900 break-all">{asset.filename}</h2>
              <div className="flex flex-wrap items-center gap-2 mt-2">
                <StatusBadge status={asset.status} />
                <StorageBadge storage={asset.storage} hideActive />
                {asset.tags.map((t) => (
                  <Link
                    key={t}
                    to={`${inManagement ? ROUTES.management : ROUTES.libraryAssets}?tag=${encodeURIComponent(t)}`}
                    className={`px-2 py-0.5 rounded-md border text-xs font-medium hover:underline ${ENTITY.asset.chip}`}
                  >
                    #{t}
                  </Link>
                ))}
              </div>
            </div>
            {!inManagement && asset.status === "completed" && (
              <Button variant="primary" icon={<Link2 size={15} />} onClick={() => setLinkOpen(true)}>
                Link to Events or Products
              </Button>
            )}
          </div>
        </div>

        <div className="space-y-4">
          {inManagement && (
            <ManageAssetPanel
              asset={asset}
              onMessage={showToast}
              onDeleted={goBackToList}
              onReplaced={(id) => navigate(ROUTES.managedAsset(id), { replace: true, state: location.state })}
            />
          )}
          <section className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm">
            <h3 className="text-sm font-semibold text-slate-900 mb-1">Connected to</h3>
            <p className="text-xs text-slate-500 mb-4">Every Event and Product this asset is linked to</p>

            <div className="mb-4">
              <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide mb-2" style={{ color: ENTITY.event.solid }}>
                <CalendarDays size={14} /> Events · {linkedEvents.length}
              </div>
              {linkedEvents.length === 0 ? (
                <p className="text-sm text-slate-400">Not linked to any event</p>
              ) : (
                <ul className="space-y-1.5">
                  {linkedEvents.map((e) => (
                    <li key={e.id}>
                      <Link to={ROUTES.libraryEvents} className={`flex items-center justify-between gap-2 px-3 py-2 rounded-lg border text-sm hover:shadow-sm ${ENTITY.event.chip}`}>
                        <span className="truncate font-medium">{e.title}</span>
                        <span className="text-xs opacity-75 shrink-0">{formatDate(e.startDate)}</span>
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div>
              <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide mb-2" style={{ color: ENTITY.product.solid }}>
                <Package size={14} /> Products · {linkedProducts.length}
              </div>
              {linkedProducts.length === 0 ? (
                <p className="text-sm text-slate-400">Not linked to any product</p>
              ) : (
                <ul className="space-y-1.5">
                  {linkedProducts.map((p) => (
                    <li key={p.id}>
                      <Link
                        to={ROUTES.product(p.id)}
                        // Product details reads the product from router state and returns to returnPath
                        state={{ product: toProductDetailsState(p), returnPath: `${location.pathname}${location.search}` }}
                        className={`block px-3 py-2 rounded-lg border text-sm font-medium truncate hover:shadow-sm ${ENTITY.product.chip}`}>
                        {p.title}
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </section>

          <section className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm">
            <h3 className="text-sm font-semibold text-slate-900 mb-2">Details</h3>
            <dl className="divide-y divide-slate-100">
              <DetailRow label="Type">{asset.mimeType}</DetailRow>
              <DetailRow label="Size">{formatBytes(asset.sizeBytes)}</DetailRow>
              {dims && <DetailRow label="Dimensions">{dims}</DetailRow>}
              {asset.durationSec ? <DetailRow label="Duration">{formatDuration(asset.durationSec)}</DetailRow> : null}
              <DetailRow label="Uploaded">{formatDate(asset.uploadedAt, true)}</DetailRow>
              <DetailRow label="Uploaded by">{asset.uploadedBy}</DetailRow>
              <DetailRow label="Asset ID"><span className="font-mono text-xs">{asset.id}</span></DetailRow>
            </dl>
            {!inManagement && (
              <Link
                to={ROUTES.managedAsset(asset.id)}
                state={{ trail: MGMT_TRAIL }}
                className="mt-4 inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-slate-900 hover:underline"
              >
                <FolderCog size={14} /> Archive, share, unlink or delete in Asset Management
              </Link>
            )}
          </section>
        </div>
      </div>

      {linkOpen && (
        <LinkModal
          assets={[asset]}
          onClose={() => setLinkOpen(false)}
          onDone={(msg) => {
            setLinkOpen(false);
            showToast(msg);
          }}
        />
      )}
    </div>
  );
};

export default AssetDetail;
